import { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

const LIST_MAX = 440;
const LIST_MIN_VISIBLE = 160;

/**
 * Text field + dropdown: filter options as you type; optional row to persist a new value via onAddNew.
 * Options list renders in a portal with position:fixed so scroll/overflow ancestors cannot clip it.
 */
export default function SearchableSelectWithAdd({
  value,
  onChange,
  options = [],
  onAddNew,
  addPrompt = "to list",
  placeholder = "Type to search…",
  style = {},
  disabled = false,
  /** When true, closing the list with an empty query commits `onChange("")` (optional fields). */
  allowEmptyCommit = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  const [adding, setAdding] = useState(false);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const norm = (s) => (s || "").trim().toLowerCase();

  const mergedOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (x) => {
      const t = (x || "").trim();
      if (!t) return;
      const k = t.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push(t);
    };
    options.forEach(push);
    if (value) push(value);
    return out;
  }, [options, value]);

  useEffect(() => {
    if (!open) setQ(value || "");
  }, [value, open]);

  const updateMenuPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 10;
    const w = Math.max(r.width, 240);
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const preferBelow = spaceBelow >= LIST_MIN_VISIBLE || spaceBelow >= spaceAbove;
    if (preferBelow) {
      const maxH = Math.min(LIST_MAX, Math.max(120, spaceBelow));
      setMenuStyle({
        position: "fixed",
        left: r.left,
        top: r.bottom + 2,
        width: w,
        maxHeight: maxH,
        zIndex: 10050,
      });
    } else {
      const maxH = Math.min(LIST_MAX, Math.max(120, spaceAbove - 4));
      setMenuStyle({
        position: "fixed",
        left: r.left,
        bottom: window.innerHeight - r.top + 4,
        width: w,
        maxHeight: maxH,
        zIndex: 10050,
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open || disabled) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, disabled, updateMenuPosition, q, mergedOptions.length]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      if (allowEmptyCommit && !norm(q)) onChange("");
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open, allowEmptyCommit, q, onChange]);

  const filtered = useMemo(() => {
    const qq = norm(q);
    if (!qq) return mergedOptions;
    return mergedOptions.filter((o) => norm(o).includes(qq));
  }, [mergedOptions, q]);

  const exactMatch = mergedOptions.some((o) => norm(o) === norm(q));
  const canAdd = typeof onAddNew === "function" && norm(q) && !exactMatch;

  const pick = (v) => {
    onChange(v);
    setQ(v);
    setOpen(false);
  };

  const handleAdd = async () => {
    const name = q.trim();
    if (!name || !onAddNew) return;
    setAdding(true);
    try {
      await onAddNew(name);
      pick(name);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Could not save";
      window.alert(msg);
    } finally {
      setAdding(false);
    }
  };

  const displayValue = open ? q : (value || "");

  const dropdown =
    open && !disabled && menuStyle ? (
      <div
        ref={dropdownRef}
        style={{
          ...menuStyle,
          overflowY: "auto",
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 8,
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        }}
      >
        {filtered.length === 0 && !canAdd && (
          <div style={{ padding: "8px 10px", fontSize: 12, color: "#9CA3AF" }}>No matches</div>
        )}
        {filtered.map((o) => (
          <button
            key={o}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              pick(o);
            }}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "9px 12px",
              border: "none",
              background: "transparent",
              fontSize: 12,
              cursor: "pointer",
              color: "#374151",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#F3F4F6";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {o}
          </button>
        ))}
        {canAdd && (
          <button
            type="button"
            disabled={adding}
            onMouseDown={(e) => {
              e.preventDefault();
              if (!adding) handleAdd();
            }}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "10px 12px",
              border: "none",
              borderTop: filtered.length ? "1px solid #F3F4F6" : "none",
              background: "#F7F1F7",
              fontSize: 12,
              fontWeight: 600,
              color: "#976793",
              cursor: adding ? "wait" : "pointer",
            }}
          >
            {adding ? "Saving…" : `+ Add “${q.trim()}” ${addPrompt}`}
          </button>
        )}
      </div>
    ) : null;

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 100, ...style }}>
      <input
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        value={displayValue}
        onFocus={() => {
          setOpen(true);
          setQ(value || "");
        }}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            setQ(value || "");
          }
          if (e.key === "Enter") {
            e.preventDefault();
            if (canAdd && !adding) handleAdd();
            else if (filtered.length === 1) pick(filtered[0]);
          }
        }}
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid #E5E7EB",
          borderRadius: 6,
          fontSize: 12,
          outline: "none",
          fontFamily: "inherit",
          background: disabled ? "#F3F4F6" : "#F9FAFB",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
      {typeof document !== "undefined" && dropdown ? createPortal(dropdown, document.body) : null}
    </div>
  );
}
